export type SessionUserType = 'startup' | 'investor';

/**
 * A reload used to treat auth `loading` as logged out and paint the guest menu.
 * Stay signed in when Firebase already has a user, or while auth is restoring a stored session.
 */
export function headerStaysSignedIn(
  firebaseUserPresent: boolean,
  authLoading: boolean,
  storedUid: string | null
): boolean {
  return firebaseUserPresent || (authLoading && storedUid !== null);
}

/** Hint the last account type only while the DB user has not arrived. A real user wins. */
export function headerHintedUserType(
  hasDbUser: boolean,
  authLoading: boolean,
  sessionUserType: SessionUserType | null
): SessionUserType | null {
  if (hasDbUser || !authLoading) return null;
  return sessionUserType;
}

/** Favorites must wait for the real investor. A header hint must not fetch and stick the badge at 0. */
export function shouldFetchInvestorFavorites(userType: string | undefined): boolean {
  return userType === 'investor';
}

/**
 * Same uid (token refresh) keeps the DB user. A different uid drops it immediately so the
 * previous person's name cannot sit on a new signup until getUser returns.
 */
export function shouldClearPreviousDbUser(fetchedForUid: string | null, nextUid: string): boolean {
  return fetchedForUid !== null && fetchedForUid !== nextUid;
}

/** Ignore a profile response that belongs to an account we have already left. */
export function isCurrentProfileResponse(requestUid: string | null, responseUid: string): boolean {
  return requestUid === responseUid;
}

/** Firebase uid wins when the React user is still the previous account. */
export function expressContextUid(
  userId: string | null | undefined,
  firebaseUid: string | null
): string | null {
  const contextIsStale = !!userId && !!firebaseUid && userId !== firebaseUid;
  return contextIsStale ? null : (userId ?? null);
}

export type ExpressAccountBind = 'ignore' | 'bind' | 'clear';

/** First bind does not wipe the form. Switching away from an account does. */
export function expressAccountBindAction(
  previousBoundId: string | null,
  nextUid: string | null
): ExpressAccountBind {
  if (previousBoundId === nextUid) return 'ignore';
  if (previousBoundId === null) return 'bind';
  return 'clear';
}

export function investorProfileBelongsToAccount(
  profileUserId: string | undefined,
  userId: string | undefined,
  firebaseUid: string | null
): boolean {
  if (!profileUserId || !userId || profileUserId !== userId) return false;
  if (firebaseUid && firebaseUid !== userId) return false;
  return true;
}

/**
 * The signup button used auth `loading`, so the first paint said "Creating Account..."
 * before a click. Only a real submit or a Stripe redirect is busy.
 */
export function signupButtonPhase(
  submitting: boolean,
  redirectingToCheckout: boolean
): 'creating' | 'redirecting' | 'ready' {
  if (submitting) return 'creating';
  if (redirectingToCheckout) return 'redirecting';
  return 'ready';
}
